import React, { useState, useEffect } from 'react';
import { currenciesCrudApi, CurrencyItem, CreateCurrencyRequest, UpdateCurrencyRequest } from '../api/currencies';
import { Plus, Pencil, Search } from 'lucide-react';
import Modal from '../components/common/Modal';

function CurrenciesPage() {
  const [currencies, setCurrencies] = useState<CurrencyItem[]>([]);
  const [filtered, setFiltered] = useState<CurrencyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CurrencyItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateCurrencyRequest>({
    code: '',
    name: '',
    numericCode: '',
    minorUnits: 2,
    isActive: true,
  });

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      currencies.filter(
        (c) =>
          c.code.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q) ||
          (c.numericCode && c.numericCode.includes(q))
      )
    );
  }, [search, currencies]);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const data = await currenciesCrudApi.getAll();
      setCurrencies(Array.isArray(data) ? data : []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch currencies');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditing(null);
    setFormData({ code: '', name: '', numericCode: '', minorUnits: 2, isActive: true });
    setError('');
    setShowForm(true);
  };

  const openEditForm = (currency: CurrencyItem) => {
    setEditing(currency);
    setFormData({
      code: currency.code,
      name: currency.name,
      numericCode: currency.numericCode || '',
      minorUnits: currency.minorUnits,
      isActive: true,
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.code.trim() || !formData.name.trim()) {
      setError('Code and Name are required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      if (editing) {
        const updateData: UpdateCurrencyRequest = {
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
          numericCode: formData.numericCode?.trim() || undefined,
          minorUnits: formData.minorUnits,
          isActive: formData.isActive,
        };
        await currenciesCrudApi.update(editing.id, updateData);
      } else {
        const createData: CreateCurrencyRequest = {
          ...formData,
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
          numericCode: formData.numericCode?.trim() || undefined,
        };
        await currenciesCrudApi.create(createData);
      }

      closeForm();
      await fetchCurrencies();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.errors?.join(', ') || err.message || 'Failed to save currency');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Currencies</h1>
          <p className="text-gray-600">Manage the list of currencies available in the system</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Currencies</h1>
        <p className="text-gray-600">Manage the list of currencies available in the system</p>
      </div>

      {/* Search + Add button */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by code, name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        <button
          onClick={openCreateForm}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Currency
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Currencies</p>
          <p className="text-2xl font-semibold text-gray-900">{currencies.length}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Numeric Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Minor Units
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((currency) => (
              <tr key={currency.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold bg-indigo-100 text-indigo-800">
                    {currency.code}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {currency.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {currency.numericCode || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {currency.minorUnits}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => openEditForm(currency)}
                    className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {search
              ? 'No currencies matching your search.'
              : 'No currencies found. Click "Add Currency" to create one.'}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={showForm}
        onClose={closeForm}
        title={editing ? 'Edit Currency' : 'Add Currency'}
        size="md"
      >
        <form onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. USD"
                maxLength={10}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numeric Code
              </label>
              <input
                type="text"
                value={formData.numericCode || ''}
                onChange={(e) =>
                  setFormData({ ...formData, numericCode: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 840"
                maxLength={5}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. US Dollar"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minor Units
            </label>
            <input
              type="number"
              min="0"
              max="8"
              value={formData.minorUnits}
              onChange={(e) =>
                setFormData({ ...formData, minorUnits: parseInt(e.target.value) || 0 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeForm}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting
                ? editing
                  ? 'Updating...'
                  : 'Creating...'
                : editing
                ? 'Update Currency'
                : 'Add Currency'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CurrenciesPage;
